/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import BankNote from './BankNote';
import PosPrintDrop from './PosPrintDrop';

function initStepper({ icon, completed }) {
  const icons_complete = {
    1: <Icon color='info'>qr_code_scanner</Icon>,
    2: <Icon color='info'>money</Icon>,
    3: <Icon color='info'>camera_alt</Icon>,
    4: <Icon color='info'>price_change</Icon>,
    5: <Icon color='info'>task_alt</Icon>,
  };
  const icons = {
    1: <Icon color='disabled'>qr_code_scanner</Icon>,
    2: <Icon color='disabled'>money</Icon>,
    3: <Icon color='disabled'>camera_alt</Icon>,
    4: <Icon color='disabled'>price_change</Icon>,
    5: <Icon color='disabled'>task_alt</Icon>,
  };
  return <div>{completed ? icons_complete[String(icon)] : icons[String(icon)]}</div>;
}

const stepperName = {
  edit: {
    name: 'สแกน',
    color: 'warning',
    icon: 'qr_code_scanner',
  },
  counting: {
    name: 'นับเงิน',
    color: 'info',
    icon: 'hourglass_top',
  },
  photo: {
    name: 'ถ่ายภาพ',
    color: 'info',
    icon: 'camera_alt',
  },
  summary: {
    name: 'ยอดนำส่ง',
    color: 'info',
    icon: 'price_change',
  },
  success: {
    name: 'เสร็จสิ้น',
    color: 'success',
    icon: 'task_alt',
  },
};

const initAttr = {
  id: '',
  name: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

function PosDropMoney({ reload, setBlockScan, sizeButton, buttonLoading, setButtonLoading }) {
  const mod = 'pos_drop';
  const app = useSelector((state) => state[APP_STATE]);
  const [attr, setAttr] = useState(initAttr);
  const [record, setRecord] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [notify, setNotify] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [imageScan, setImageScan] = useState('');
  const [imageQR, setImageQR] = useState('');
  const [imageQrPhoto, setImageQrPhoto] = useState('');
  const [image, setImage] = useState([]);
  const [cboCash, setCboCash] = useState([]);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState('');
  const { t, i18n } = useTranslation();
  const printRef = useRef();
  const btnRef = useRef(null);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const close = () => {
    setModalAdd(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const view = () => {
    setLoading(true);
    setBlockScan(true);
    setActiveStep(1);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setCboCash(result.cash);
          setImageScan(result.mobile);
          setImageQR(result.qrcode);
          setImageQrPhoto(result.qrphoto);
          setImage(result.image);
          // printRef.current.print();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editTotal = (row) => {
    const data = [];
    let result = 0;

    list.map((item) => (row.id === item.id ? null : data.push(item)));

    data.push(row);
    setList(data);

    data.forEach(function (item) {
      result += item.total * 1;
    });

    setTotal(result.toString());
  };

  const scan = () => {
    setStepLoading(true);
    FetchData('OPTIONS', `/${mod}/scan`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setActiveStep(activeStep + 1);
          printRef.current.print();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setStepLoading(false);
      });
  };

  const counting = () => {
    setStepLoading(true);
    FetchData('OPTIONS', `/${mod}/counting`, app.token, {
      list: list,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setActiveStep(activeStep + 1);
          printRef.current.print();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setStepLoading(false);
      });
  };

  const summary = () => {
    setStepLoading(true);
    FetchData('OPTIONS', `/${mod}/summary`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setRecord(result.list);
          setImage(result.image);
          setActiveStep(activeStep + 1);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setStepLoading(false);
      });
  };

  const success = () => {
    setStepLoading(true);
    FetchData('OPTIONS', `/${mod}/success`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setActiveStep(activeStep + 1);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setStepLoading(false);
      });
  };

  const handleNext = () => {
    if (activeStep === 1) {
      scan();
    } else if (activeStep === 2) {
      counting();
    } else if (activeStep === 3) {
      summary();
    } else if (activeStep === 4) {
      success();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 3) {
      setTotal('');
    }

    setActiveStep(activeStep - 1);
  };

  const renderStepper = () => {
    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={initStepper}>
            <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
          </StepLabel>
        </Step>
      );
    };

    return (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('counting')}
          {stepLevel('photo')}
          {stepLevel('summary')}
          {stepLevel('success')}
        </Stepper>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2} lg={1}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={3} lg={4}>
            <MDBox className='card' p={2}>
              <MDBox>
                <img src={imageQrPhoto} alt='' width='100%' />
              </MDBox>
              <MDBox align='center'>
                <MDTypography>SCAN QR CODE</MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={1} lg={1}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            <img src={imageScan} alt='' width='100%' />
          </Grid>
          <Grid item xs={12} md={2} lg={1}>
            &nbsp;
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderScan = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2} lg={1}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={3} lg={4}>
            <MDBox className='card' p={2}>
              <MDBox>
                <img src={imageQR} alt='' width='100%' />
              </MDBox>
              <MDBox align='center'>
                <MDTypography>SCAN QR CODE</MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={1} lg={1}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            <img src={imageScan} alt='' width='100%' />
          </Grid>
          <Grid item xs={12} md={2} lg={1}>
            &nbsp;
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderImage = () => {
    if (image.length === 0) return null;

    return (
      <MDBox className='card' p={2} mb={2}>
        <MDBox align='center' mb={2}>
          <MDTypography variant='h5'>ภาพประกอบ</MDTypography>
        </MDBox>
        <Grid container spacing={2}>
          {image.map((item) => (
            <Grid item xs={12} md={12} key={item.id}>
              <MDBox sx={{ borderRadius: '5px', overflow: 'hidden' }}>
                <img src={item.image} alt={item.title} width='100%' />
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    );
  };

  const renderSlip = () => {
    return (
      <MDBox className='card' p={2}>
        <MDBox align='center' mb={2}>
          <MDTypography variant='h5'>สรุป</MDTypography>
        </MDBox>
        <Grid container>
          <Grid item xs={12}>
            <MDTypography variant='body' className='underline'>
              ธนบัตร
            </MDTypography>
          </Grid>
          <Grid item xs={12}>
            {record.map((item) => (
              <Grid container key={item.id}>
                <Grid item xs={6}>
                  {item.name}
                </Grid>
                <Grid item xs={6} align='right'>
                  {Func.numberDisplay(item.quantity)} ใบ
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12} align='right'>
            <MDTypography variant='body'>รวม : {Func.numberDisplay(total, 2)} บาท</MDTypography>
          </Grid>
          <Grid item xs={12} align='right'>
            <MDTypography variant='body'>{Func.dateTimeDisplay(attr.date_now)}</MDTypography>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderSummary = () => {
    return (
      <MDBox>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            {renderSlip()}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderImage()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderCount = () => {
    return (
      <MDBox mt={1}>
        <MDBox>
          <MDBox>
            <Grid container spacing={2}>
              {cboCash.map((row) => (
                <Grid item xs={12} sm={12} md={6} key={row.id}>
                  <BankNote parent={row} callback={editTotal} />
                </Grid>
              ))}
            </Grid>
          </MDBox>
          <MDBox mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={8} align='right'>
                <MDTypography variant='h4'>รวม</MDTypography>
              </Grid>
              <Grid item xs={6} md={4} align='right'>
                <MDTypography variant='h4'>{Func.numberDisplay(total)} บาท</MDTypography>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  const renderFinish = () => {
    return (
      <MDBox mt={3}>
        <Grid container spacing={1}>
          <Grid item xs={12} align='center'>
            <Alert severity='success'>บันทึกข้อมูลเรียบร้อยแล้ว</Alert>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderStep = () => {
    if (activeStep === 1) return renderScan();
    if (activeStep === 2) return renderCount();
    if (activeStep === 3) return renderPhoto();
    if (activeStep === 4) return renderSummary();
    if (activeStep === 5) return renderFinish();

    return null;
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          <i className='fad fa-envelope-open-dollar color-danger' /> &nbsp; ดรอปเงิน
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {renderStepper()}
              </Grid>
              <Grid item xs={12}>
                <MDBox height={420}>{renderStep()}</MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              {activeStep > 4 ? null : (
                <MDButton
                  variant='text'
                  color='dark'
                  disabled={activeStep < 2}
                  onClick={handleBack}
                >
                  <Icon>chevron_left</Icon>&nbsp;ย้อนกลับ
                </MDButton>
              )}
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              {activeStep === 5 ? (
                <MDButton
                  variant='text'
                  color='dark'
                  onClick={() => setModalAdd(false)}
                  disabled={stepLoading}
                >
                  {stepLoading ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : (
                    <Icon>close</Icon>
                  )}
                  ปิด &nbsp;
                </MDButton>
              ) : (
                <MDButton
                  variant='text'
                  color='info'
                  disabled={activeStep > 4 || stepLoading}
                  onClick={handleNext}
                >
                  ถัดไป &nbsp;
                  {stepLoading ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : (
                    <Icon>chevron_right</Icon>
                  )}
                </MDButton>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <MDBox>
      <Tooltip title='Drop money'>
        <MDButton
          variant='contained'
          color='dark'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={() => {
            setModalAdd(true);
            view();
          }}
          ref={btnRef}
          disabled={loading || buttonLoading}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-envelope-open-dollar' />
          )}
          &nbsp; ดรอปเงิน
        </MDButton>
      </Tooltip>
      <PosPrintDrop ref={printRef} parent={attr} />
      {notify}

      {renderAdd()}
    </MDBox>
  );
}

PosDropMoney.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosDropMoney;
